import { Client } from '../models';
import { I18n } from '../wrappers/I18n';
import { dateFormatter } from '../utilities/dateFormatter';
import { OrderRegisterStatusType } from '../types';
import { SERVER_PDF_ERROR, serverPdf } from './ServerPdfService';
import { savePdfFile } from './pdfFile';
/**
 * Receipts as server PDFs: which documents may leave the venue, how they are
 * named and laid out, and the save / e-mail actions the receipt screens call.
 * The PDF is the reprint (`isCopy`) in the format on screen — the regular
 * format as one 80mm page as tall as the receipt, A4 on A4 — so no fiscal
 * event is created.
 */
class ReceiptPdf {
    /** Offered while the server renders receipts, for a document the market lets leave the venue. */
    isOffered(order) {
        return !!order && serverPdf.supports('receipt') && this.isExportable(order);
    }
    /**
     * A TaxCore document only once it is fiscalized (the request and the response stored) —
     * before that there is no fiscal receipt to hand out; elsewhere every listed document.
     */
    isExportable(order) {
        if (I18n.c('order.registerMethod') === 'taxcore') {
            return order.register_status === OrderRegisterStatusType.success
                && !!order.register_request
                && !!order.register_response;
        }
        return true;
    }
    /** The document's own word — Račun, Predračun, Knjižno odobrenje, Refundacija — never its payment type ("Prenos na račun"). */
    label(order) {
        const kind = this.kind(order);
        if (kind === 'proforma')
            return String(I18n.t('general.proforma', 1));
        if (kind === 'creditNote')
            return String(I18n.t('general.credit_note'));
        if (kind === 'refund')
            return String(I18n.t('pdf.refund_document'));
        return String(I18n.t('general.receipt', 1));
    }
    /**
     * The number the document prints as its own: TaxCore's register number, Montenegro's full
     * invoice number (venue / number / year / register), else the local number.
     */
    number(order) {
        const method = I18n.c('order.registerMethod');
        if (method === 'taxcore' && order.register_number) {
            return String(order.register_number);
        }
        if (method === 'mehr' && order.number) {
            return order.getFullNumber() || String(order.number);
        }
        return String(order.number ?? '');
    }
    /**
     * The register number for TaxCore, "<Document>-<local number>" otherwise — without the
     * extension, and never Montenegro's full number: its slashes would make a path of the name.
     */
    fileName(order) {
        return I18n.c('order.registerMethod') === 'taxcore' && order.register_number
            ? String(order.register_number)
            : `${this.label(order)}-${order.number}`;
    }
    /** "Račun <number> · <total>" — what the e-mail sheet shows under its title. */
    documentLine(order) {
        return `${this.label(order)} ${this.number(order)} · ${order.getToPay().abs().toFormat()}`;
    }
    document(order, options = {}) {
        return {
            kind: 'receipt',
            profile: options.forceA4Format ? 'receipt_a4' : 'receipt_roll',
            title: `${this.label(order)} ${this.number(order)}`,
            period: '',
            fileName: this.fileName(order),
            pageLabel: '',
            lang: String(I18n.$i18n?.locale?.value || 'sr'),
        };
    }
    /** Renders on the server and hands the file to the user (download / share sheet). */
    async save(source, endpointBase = '') {
        const printable = await this.printable(source);
        if (!printable)
            return { success: false, error: SERVER_PDF_ERROR.general };
        const document = this.document(source.order, source.options);
        const result = await serverPdf.render(printable, document, endpointBase);
        if (!result.success)
            return result;
        if (!result.pdf)
            return { success: false, error: SERVER_PDF_ERROR.general };
        return await savePdfFile(result.pdf, document.fileName)
            ? result
            : { success: false, error: SERVER_PDF_ERROR.saveFailed };
    }
    /**
     * Mails the receipt to the customer. The server saves the address to the order's client;
     * the client it returns is merged into the models store so the next prefill has it.
     */
    async email(source, to, endpointBase = '') {
        const printable = await this.printable(source);
        if (!printable)
            return { success: false, error: SERVER_PDF_ERROR.general };
        const document = { ...this.document(source.order, source.options), fields: await this.emailFields(source.order) };
        const result = await serverPdf.email(printable, document, to, endpointBase);
        if (result.success && result.client) {
            Client.update(result.client);
        }
        return result;
    }
    /**
     * The order and the facts the mail states — display strings, so the server knows no receipt
     * vocabulary; `sale` tells it whether the mail thanks the customer for a purchase. A refund's
     * amount is stated as the document states it, without the minus sign.
     */
    async emailFields(order) {
        const { formatDateTime } = dateFormatter();
        // No market verifies a proforma — never offer a link that resolves to "not found".
        const verificationUrl = order.isProforma() ? '' : String((await order.generateQRCodeString()) || '');
        return {
            'order': String(order.hash),
            'receipt[label]': this.label(order),
            'receipt[number]': this.number(order),
            'receipt[date]': order.last_activity_at ? formatDateTime(order.last_activity_at) : '',
            'receipt[total]': order.getToPay().abs().toFormat(),
            'receipt[sale]': this.kind(order) === 'sale' ? '1' : '0',
            'verification_url': verificationUrl,
        };
    }
    /** What the document is to its customer; money going back is a refund (a corrective invoice in Montenegro). */
    kind(order) {
        if (order.isProforma())
            return 'proforma';
        if (order.isCreditNote())
            return 'creditNote';
        if (order.getToPay().toUnit() < 0)
            return 'refund';
        return 'sale';
    }
    /** The reprint the screen shows; a failure becomes a result the screens can toast. */
    async printable(source) {
        try {
            return await source.order.getPrintable({ ...source.options, isCopy: true }, source.venue);
        }
        catch (error) {
            console.error('Receipt PDF: building the receipt failed', error);
            return null;
        }
    }
}
export const receiptPdf = new ReceiptPdf();
//# sourceMappingURL=receiptPdf.js.map